import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import StickySaveBar from "@/components/admin/StickySaveBar";

export default function AdminResults() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/results/settings");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const updRow = (i, patch) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const saveAll = async () => {
    try {
      setSaving(true);
      await Promise.all(
        rows.map((r) =>
          api.put(`/results/settings/${r.game_id}`, {
            game_id: r.game_id,
            visible: !!r.visible,
            top_n: Math.max(1, Math.min(1000, parseInt(r.top_n) || 10)),
            show_name: !!r.show_name,
            show_nationality: !!r.show_nationality,
            show_school: !!r.show_school,
            show_best_time: !!r.show_best_time,
          })
        )
      );
      toast.success("Configurações guardadas");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = async (gameId, scope, gameName) => {
    try {
      const r = await api.get(`/admin/export/${scope}/${gameId}.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const safe = (gameName || "game").replace(/[^a-zA-Z0-9_-]/g, "_");
      a.download = `${scope}_${safe}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`CSV ${scope} descarregado`);
    } catch (e) {
      toast.error(`Falhou a exportação: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">Resultados públicos</h1>
        <Button data-testid="btn-save-results" onClick={saveAll} disabled={saving} className="bg-slate-900">
          <Save className="w-4 h-4 mr-2" /> Guardar tudo
        </Button>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Configura, para cada jogo, se aparece na página pública de Resultados, quantos utilizadores mostrar (Top N) e que colunas exibir.
      </p>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase">Jogo</th>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase">Status</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Visível</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Top N</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Nome</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Nacionalidade</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Escola</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">Melhor tempo</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase">CSV</th>
            </tr>
          </thead>
          <tbody data-testid="results-settings-table">
            {rows.map((r, i) => (
              <tr key={r.game_id} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{r.game_name}</td>
                <td className="px-3 py-2 text-slate-500">{r.game_status}</td>
                <td className="px-3 py-2 text-center">
                  <Switch
                    data-testid={`visible-${r.game_id}`}
                    checked={!!r.visible}
                    onCheckedChange={(v) => updRow(i, { visible: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Input
                    type="number"
                    min={1}
                    max={1000}
                    value={r.top_n ?? 10}
                    onChange={(e) => updRow(i, { top_n: +e.target.value })}
                    className="w-20 mx-auto text-center"
                    data-testid={`top-${r.game_id}`}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Switch
                    data-testid={`show-name-${r.game_id}`}
                    checked={!!r.show_name}
                    onCheckedChange={(v) => updRow(i, { show_name: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Switch
                    data-testid={`show-nat-${r.game_id}`}
                    checked={!!r.show_nationality}
                    onCheckedChange={(v) => updRow(i, { show_nationality: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Switch
                    data-testid={`show-school-${r.game_id}`}
                    checked={!!r.show_school}
                    onCheckedChange={(v) => updRow(i, { show_school: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <Switch
                    data-testid={`show-time-${r.game_id}`}
                    checked={!!r.show_best_time}
                    onCheckedChange={(v) => updRow(i, { show_best_time: v })}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() => downloadCsv(r.game_id, "competition", r.game_name)}
                      data-testid={`csv-comp-${r.game_id}`}
                      title="CSV de Competição"
                    >
                      <Download className="w-3 h-3 mr-1" /> Comp.
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() => downloadCsv(r.game_id, "training", r.game_name)}
                      data-testid={`csv-train-${r.game_id}`}
                      title="CSV de Treino"
                    >
                      <Download className="w-3 h-3 mr-1" /> Treino
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Sem jogos</td></tr>}
          </tbody>
        </table>
      </div>

      <StickySaveBar>
        <Button data-testid="btn-save-results-sticky" onClick={saveAll} disabled={saving} className="bg-slate-900">
          <Save className="w-4 h-4 mr-2" /> Guardar tudo
        </Button>
      </StickySaveBar>
    </div>
  );
}
